<script lang="ts">
	//Formating
	import { format } from 'src/services/i18n';
	//Helpers
	import {
		capFirstChar,
		formatSnakeCaseToTitle,
		getErrorOrInfoMessageForMinAndMaxUnit,
	} from 'src/helpers/utilities';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { getElevation, getTimeZoneName, validateGeolocation } from 'src/helpers/geolocalion_helper';
	import { postReq } from 'src/services/http';
	import { getNumberInputConfig } from 'src/helpers/number_input_helpers';
	import { getDeviceLocation } from 'src/components/WeatherStation/weather_station_helpers';
	import {
		convertElevation,
		convertElevationFromM,
		convertElevationToM,
	} from 'src/helpers/units/elevation_units';
	//Stores
	import { devices } from 'src/stores/lists/devices';
	//Maps
	import SelectDeviceLocation from 'src/components/Maps/SelectDeviceLocation.svelte';
	//Design system components
	import { Button, Input, Text } from '@Shelly/shelly-design-system';
	//Types
	import type { Device } from 'src/types/Device';
	import type { SettingRequest } from 'src/types/requests/settings/setting';

	export let device: Device;

	const MIN_ELEVATION_OFFSET = -100;
	const MAX_ELEVATION_OFFSET = 100;

	const unit = device?.ht_ui?.elevation_unit || 'm';
	const minElevation = convertElevationFromM(MIN_ELEVATION_OFFSET, unit);
	const maxElevation = convertElevationFromM(MAX_ELEVATION_OFFSET, unit);

	const saveLocationSettings = async () => {
		waitForResponseSave = true;
		const timeZone = (await getTimeZoneName(latitude, longitude)) || 'UTC';
		elevation = (await getElevation(latitude, longitude)) || 0;
		const location = {
			lat: latitude,
			lng: longitude,
			tz: timeZone,
			ele: elevation,
			ele_offset: convertElevationToM(eleOffset, unit),
		};
		const res: SettingRequest = await postReq('interface/device/set_ws_location', {
			id: device.id,
			location: JSON.stringify(location),
		});
		if (res?.isok) {
			devices.update({ ...device, location });
			locationSettings = location;
		}
		onSettingsResponse(res, device);
		waitForResponseSave = false;
	};

	const clearLocationSettings = async () => {
		waitForResponseClear = true;
		const res: SettingRequest = await postReq('interface/device/set_ws_location', {
			id: device.id,
			reset: 1,
		});
		if (res?.isok) {
			delete device.location;
			devices.update(device);
			locationSettings = getDeviceLocation(device);
			latitude = locationSettings.lat;
			longitude = locationSettings.lng;
			elevation = 0;
			eleOffset = 0;
		}
		onSettingsResponse(res, device);
		waitForResponseClear = false;
	};

	let waitForResponseSave = false;
	let waitForResponseClear = false;
	let showModal = false;
	let locationSettings: Device['location'] = getDeviceLocation(device);
	let latitude: number = locationSettings.lat;
	let longitude: number = locationSettings.lng;
	let elevation: number = locationSettings.ele ?? 0;
	let eleOffset: number = convertElevationFromM(locationSettings.ele_offset ?? 0, unit);

	$: disableSaveBtn =
		waitForResponseSave ||
		!validateGeolocation(latitude, longitude) ||
		(locationSettings?.lat == latitude &&
			locationSettings?.lng == longitude &&
			locationSettings?.ele_offset == eleOffset);

	$: minMaxElevationOffset = getErrorOrInfoMessageForMinAndMaxUnit(
		eleOffset,
		minElevation,
		maxElevation,
		unit,
	);
</script>

<Text styleType="body-small" class="mb-xs">
	{formatSnakeCaseToTitle($format('location_latitude', { default: 'latitude' }))}:
</Text>
<Input
	aria-label={$format('input_latitude', { default: 'Input Latitude' })}
	{...getNumberInputConfig()}
	class="mb-s"
	size="m"
	icon="fas fa-globe"
	value={latitude.toString()}
	min={-90}
	max={90}
	step={0.000001}
	on:input={(e) => (latitude = Number(e.target['value']))}
	on:change={async () => (elevation = (await getElevation(latitude, longitude)) || 0)}
	id="input-hc7vp80"
/>

<Text styleType="body-small" class="mb-xs">
	{formatSnakeCaseToTitle($format('location_longtitude', { default: 'longitude' }))}:
</Text>
<Input
	{...getNumberInputConfig()}
	class="mb-s"
	size="m"
	icon="fas fa-globe"
	value={longitude.toString()}
	aria-label={$format('input_longitude', { default: 'Input Longitude' })}
	min={-180}
	max={180}
	step={0.000001}
	on:input={(e) => (longitude = Number(e.target['value']))}
	on:change={async () => (elevation = (await getElevation(latitude, longitude)) || 0)}
	id="input-wy5rtcr"
/>

<Text styleType="body-small" class="mb-s text-secondary">
	{formatSnakeCaseToTitle($format('label_elevation', { default: 'Elevation' }))}: {convertElevation(
		elevation,
		unit,
	)}
</Text>
<Text styleType="body-small" class="mb-xs">
	{formatSnakeCaseToTitle($format('label_elevation_offset', { default: 'Elevation offset' }))}:
</Text>
<Input
	aria-label={$format('input_elevation_offset', { default: 'Input Elevation Offset' })}
	{...getNumberInputConfig()}
	class="mb-s"
	size="m"
	icon="far fa-mountain"
	value={eleOffset.toString()}
	min={minElevation}
	max={maxElevation}
	step={0.1}
	info={minMaxElevationOffset.info}
	error={minMaxElevationOffset.error}
	on:input={(e) => (eleOffset = Number(e.target['value']))}
	id="input-r01git7"
/>
<Button
	size="m"
	class="mb-s"
	width="full"
	variant="secondary"
	disabled={waitForResponseSave || waitForResponseClear}
	on:click={() => {
		showModal = true;
	}}
>
	{capFirstChar($format('select_map_location'))}
</Button>
<Button
	size="m"
	width="full"
	class="mb-s"
	loading={waitForResponseSave}
	disabled={(device?.location && disableSaveBtn) || minMaxElevationOffset.error}
	on:click={saveLocationSettings}>{$format('button_save', { default: 'Save' })}</Button
>
<Button
	size="m"
	width="full"
	variant="danger"
	loading={waitForResponseClear}
	disabled={!device?.location}
	on:click={clearLocationSettings}
	>{$format('button_clear_location', { default: 'Clear Location' })}</Button
>
{#if showModal}
	<SelectDeviceLocation
		bind:showModal
		lat={latitude}
		lng={longitude}
		on:change={async (e) => {
			latitude = Number(e.detail.lat.toFixed(6));
			longitude = Number(e.detail.lng.toFixed(6));
			elevation = (await getElevation(latitude, longitude)) || 0;
		}}
	/>
{/if}
